import { create } from 'zustand';
import { addData } from '../services/centralized';

interface SettingsState {
  units: {
    length: string;
    force: string;
    stress: string;
    temperature: string;
  };
  display: {
    theme: string;
    fontSize: number;
    gridSize: number;
    snapTolerance: number;
  };
  analysis: {
    defaultSolver: string;
    convergenceTolerance: number;
    maxIterations: number;
  };
  performance: {
    enableHardwareAcceleration: boolean;
    maxCores: number;
    memoryLimit: number;
  };
}

interface SettingsActions {
  updateUnits: (units: Partial<SettingsState['units']>) => void;
  updateDisplay: (display: Partial<SettingsState['display']>) => void;
  updateAnalysis: (analysis: Partial<SettingsState['analysis']>) => void;
  updatePerformance: (performance: Partial<SettingsState['performance']>) => void;
  resetToDefaults: () => void;
  loadSettings: () => void;
  saveSettings: () => void;
}

const defaultSettings: SettingsState = {
  units: {
    length: 'm',
    force: 'kN',
    stress: 'MPa',
    temperature: 'C',
  },
  display: {
    theme: 'system',
    fontSize: 14,
    gridSize: 1.0,
    snapTolerance: 0.1,
  },
  analysis: {
    defaultSolver: 'sparse',
    convergenceTolerance: 1e-6,
    maxIterations: 100,
  },
  performance: {
    enableHardwareAcceleration: true,
    maxCores: 4,
    memoryLimit: 4096,
  },
};

export const useSettingsStore = create<SettingsState & SettingsActions>((set, get) => ({
  ...defaultSettings,
  
  updateUnits: (units) => set((state) => ({
    units: { ...state.units, ...units }
  })),
  
  updateDisplay: (display) => set((state) => ({
    display: { ...state.display, ...display }
  })),
  
  updateAnalysis: (analysis) => set((state) => ({
    analysis: { ...state.analysis, ...analysis }
  })),
  
  updatePerformance: (performance) => set((state) => ({
    performance: { ...state.performance, ...performance }
  })),
  
  resetToDefaults: () => set(() => ({ ...defaultSettings })),
  
  loadSettings: () => {
    try {
      const stored = localStorage.getItem('atcivil-settings');
      if (stored) {
        const settings = JSON.parse(stored);
        set(() => ({ ...defaultSettings, ...settings }));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  },
  
  saveSettings: () => {
    try {
      const settings = get();
      const { updateUnits, updateDisplay, updateAnalysis, updatePerformance, resetToDefaults, loadSettings, saveSettings, ...settingsToSave } = settings;
              localStorage.setItem('atcivil-settings', JSON.stringify(settingsToSave));
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  },
}));

// Subscribe to SettingsStore and publish to centralized store
useSettingsStore.subscribe((state) => {
  addData("settings", {
    units: state.units,
    display: state.display,
    analysis: state.analysis,
    performance: state.performance,
  });
  console.log(
    "✅ [SettingsStore→Centralized] Published settings:",
    state.units.length,
    state.units.force
  );
});
